import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { hasSession } from '@/features/auth/session'
import { JoinPage } from '@/features/join/join-page'

/**
 * The invited member of staff's link, `/join/<token>`.
 *
 * Outside `_app` on purpose: no auth guard, no app shell, no dealership header. The person
 * opening it is usually not a member of anything yet, and the API resolves the dealership from
 * the token's hash — there is nothing to look up before it renders and nothing to redirect to
 * when there is no session.
 *
 * Deliberately not `/s/$token`, which is the customer's signing page. The two links go to
 * different people and end in different places, and one route that had to tell them apart would
 * be one route that could get it wrong.
 *
 * The loader spends the refresh cookie before the page decides which of its four states to draw.
 * Without it, somebody opening the link in the installed app after a night with the laptop shut
 * holds an expired access token, would be read as signed out, and would be offered a second
 * account for the address they already have.
 */
export const Route = createFileRoute('/join/$token')({
  loader: async () => {
    await hasSession()
  },
  component: JoinRoute,
})

function JoinRoute() {
  const { token } = Route.useParams()
  const navigate = useNavigate()
  // A different link is a different invitation: the key throws away the previous one's cache and
  // form state rather than showing one dealership's offer while the next one loads.
  return (
    <JoinPage
      key={token}
      token={token}
      onJoined={() => void navigate({ to: '/', replace: true })}
    />
  )
}
